import React, { useEffect, useState } from 'react'
import useForm from '@teqplay/react-useform'
import PropTypes from 'prop-types'
import { cloneDeep, set, isEmpty } from 'lodash'

import InputTextField from '../../../fields/inputtextfield/InputTextField'
import InputDateField from '../../../fields/inputdatefield/InputDateField'
import InputNumberField from '../../../fields/inputnumberfield/InputNumberField'
import DisplayField from '../../../fields/displayfield/DisplayField'
import { validateCargoOperationsYara, getErrorMessageForFormField } from '../../../../utils/formUtils'
import {
  fetchCargoOperationsDetailsFromBackendRest,
  insertCargoOperationsDetailsInBackendRest,
  updateCargoOperationsDetailsInBackendRest
} from '../../../../js/rest'
import { handleFetchError } from '../../../../js/sentry'
import Modal from '../../../app/customModal/Modal'
import ErrorMessage from '../../../errormessage/ErrorMessage'
import { CARGO_DETAILS_TYPE } from '../../../../js/constants'
import InputSelectField from '../../../fields/inputselectfield/InputSelectField'

const apiUrlSuffix = CARGO_DETAILS_TYPE.yara

// fill in nested fields
const defaultCargoOperationsDetails = {
  inspection: {}
}

const emptyDropdownSelection = Object.freeze({ displayText: '', value: '' })
export default function CargoOperationsDetailsYara(props) {
  const [hasBeenValidatedOnce, setHasBeenValidatedOnce] = useState(false)

  const { values, errors, handleSubmit, setAllValues, triggerValidation } = useForm({
    initialValues: defaultCargoOperationsDetails,
    validate,
    onValidSubmit
  })

  const {
    identifiers: { terminalVisitId }
  } = props

  useEffect(() => {
    if (terminalVisitId) {
      // Fetch the terminal visit from the backend
      fetchCargoOperationsDetails()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terminalVisitId])

  useEffect(() => {
    if (hasBeenValidatedOnce) {
      triggerValidation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])

  /**
   * When the value of a form input field changes
   */
  function onFormInputChange(value, field) {
    const cargoDetails = cloneDeep(values)

    set(cargoDetails, field, value)

    // clear inspection results if not scheduled
    if (field === 'inspection.scheduled' && ['not-needed', 'no'].includes(value)) {
      set(cargoDetails, 'inspection.result', '')
    }
    setAllValues(cargoDetails)
  }

  const {
    // @ts-ignore
    phoneNumber,
    // @ts-ignore
    arrivalTime,
    // @ts-ignore
    startTime,
    // @ts-ignore
    stopTime,
    // @ts-ignore
    actualAmount,
    // @ts-ignore
    vesselName,
    // @ts-ignore
    imoNumber,
    // older db entries do not have 'inspection' field
    inspection = {}
  } = values

  const { readOnly } = props
  return (
    <>
      <Modal.Content>
        <div className="detail-view-section">
          <h1>Ship</h1>
          <div className="detailview double-view">
            <DisplayField title={'Name'} value={vesselName} />
            <DisplayField title={'IMO'} value={imoNumber} />
          </div>
          <div className="detailview">
            <InputTextField
              name={'Contact number'}
              fieldName={'phoneNumber'}
              fieldValue={phoneNumber}
              error={getErrorMessageForFormField(errors, 'phoneNumber')}
              required={false}
              onChange={onFormInputChange}
              readOnly={readOnly}
            />
          </div>
          <div className="detailview">
            <InputDateField
              name={'Arrival time'}
              fieldName={'arrivalTime'}
              fieldValue={arrivalTime}
              error={getErrorMessageForFormField(errors, 'arrivalTime')}
              required={false}
              onChange={onFormInputChange}
              readOnly={readOnly}
            />
          </div>
        </div>
        <div className="detail-view-section">
          <h1>Cargo</h1>
          <div className="detailview double-view">
            <InputDateField
              name={'Cargo start'}
              fieldName={'startTime'}
              fieldValue={startTime}
              error={getErrorMessageForFormField(errors, 'startTime')}
              required={false}
              onChange={onFormInputChange}
              readOnly={readOnly}
            />

            <InputDateField
              name={'Cargo end'}
              fieldName={'stopTime'}
              fieldValue={stopTime}
              error={getErrorMessageForFormField(errors, 'stopTime')}
              required={false}
              onChange={onFormInputChange}
              readOnly={readOnly}
              pickerAlignLeft
            />
          </div>
          <div className="detailview">
            <InputNumberField
              name={'Actual amount (mt)'}
              fieldName={'actualAmount'}
              fieldValue={actualAmount}
              required={false}
              error={getErrorMessageForFormField(errors, 'actualAmount')}
              onChange={onFormInputChange}
              readOnly={readOnly}
            />
          </div>
        </div>
        <div className="detail-view-section">
          <h1>Inspection</h1>
          <div className="detailview double-view">
            <InputSelectField
              title={'Has been scheduled'}
              fieldName={'inspection.scheduled'}
              fieldValue={inspection.scheduled}
              onChange={onFormInputChange}
              readOnly={readOnly}
              emptyOption
              options={[
                { displayText: 'Not needed', value: 'not-needed' },
                { displayText: 'Yes', value: 'yes' },
                { displayText: 'No', value: 'no' }
              ]}
            />
            <InputSelectField
              title={'Inspector'}
              fieldName={'inspection.inspector'}
              fieldValue={inspection.inspector}
              onChange={onFormInputChange}
              readOnly={readOnly}
              options={[
                emptyDropdownSelection,
                { displayText: 'Yara', value: 'yara' },
                { displayText: 'Intertek', value: 'intertek' },
                { displayText: 'PMI', value: 'pmi' }
              ]}
            />
          </div>
          <div className="detailview">
            <InputSelectField
              title={'Result'}
              fieldName={'inspection.result'}
              fieldValue={inspection.result}
              onChange={onFormInputChange}
              readOnly={readOnly || ['not-needed', 'no'].includes(inspection.scheduled)}
              options={[
                emptyDropdownSelection,
                { displayText: 'Passed', value: 'passed' },
                { displayText: 'Failed', value: 'failed' }
              ]}
            />
          </div>
          <div className="detailview">
            <InputTextField
              name={'Comment'}
              fieldName={'inspection.comment'}
              fieldValue={inspection.comment}
              error={false}
              required={false}
              readOnly={readOnly}
              onChange={onFormInputChange}
            />
          </div>
        </div>
      </Modal.Content>
      <div className="footer">
        {!isEmpty(errors) ? (
          <ErrorMessage message={'Please make sure that you filled in all fields correctly'} />
        ) : null}

        <Modal.Buttons>
          {!readOnly ? (
            <div className="no-btn btn" onClick={handleCancel}>
              <span>Cancel</span>
            </div>
          ) : null}
          {!readOnly && (
            <div className="yes-btn btn" onClick={handleSubmit}>
              <span>Save</span>
            </div>
          )}
        </Modal.Buttons>
      </div>
    </>
  )

  function validate() {
    if (!hasBeenValidatedOnce) {
      setHasBeenValidatedOnce(true)
    }
    const formErrors = validateCargoOperationsYara(values)
    return formErrors
  }

  function onValidSubmit() {
    saveForm()
  }

  async function saveForm() {
    try {
      // @ts-ignore
      if (values && values._id) {
        const updatedCargoDetails = await updateCargoOperationsDetailsInBackendRest(
          props.identifiers,
          apiUrlSuffix,
          values,
          props.authToken,
          props.customFetch
        )
        setAllValues(updatedCargoDetails)
      } else {
        const insertedCargoDetails = await insertCargoOperationsDetailsInBackendRest(
          props.identifiers,
          apiUrlSuffix,
          values,
          props.authToken,
          props.customFetch
        )
        setAllValues(insertedCargoDetails) // mostly important to fill in the _id that the document got after insert
      }
      props.closeModal()
    } catch (err) {
      handleFetchError(err)
    }
  }

  function handleCancel() {
    props.closeModal()
  }

  async function fetchCargoOperationsDetails() {
    try {
      // backend gives an empty object back instead of 404 if it doesn't exist
      const cargoDetails = await fetchCargoOperationsDetailsFromBackendRest(
        props.identifiers,
        apiUrlSuffix,
        props.authToken,
        props.customFetch
      )

      setAllValues(insertStaticInformation(cargoDetails))
    } catch (err) {
      // 404 not found is no problem, in that case we simply don't have a document yet.
      // Let's create one.
      setAllValues(insertStaticInformation(defaultCargoOperationsDetails))

      if (err.status !== 404) {
        handleFetchError(err)
      }
    }
  }

  function insertStaticInformation(values) {
    return {
      ...values,
      terminalVisitId: props.identifiers.terminalVisitId,
      orderId: props.identifiers.orderId,
      vesselName: props.ship && props.ship.name ? props.ship.name : '',
      imoNumber: props.ship && props.ship.imo ? props.ship.imo : ''
    }
  }
}

CargoOperationsDetailsYara.propTypes = {
  identifiers: PropTypes.shape({
    terminalId: PropTypes.string.isRequired,
    terminalVisitId: PropTypes.string.isRequired,
    orderId: PropTypes.string.isRequired
  }).isRequired,
  order: PropTypes.object.isRequired,
  ship: PropTypes.object,
  authToken: PropTypes.string,
  customFetch: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired
}
